'use client';
import { CaptchaRequestMessage, isCaptchaValidationError } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { trackFormSubmitSuccess } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { Heading } from '../ui/heading';
import { Paragraph } from '../ui/paragraph';
import Link from '@eliadmin/core/src/components/link';
import { ButtonIcon } from '../ui/button-icon';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { getGdprUrl } from '@eliadmin/core/src/utils/gdpr-tos/get-tos-gdpr-url';

export function Newsletter() {
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();
  const generalLayout = useGlobalStore((s) => s.generalLayout);

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(true);
  const [isSubscribed, setIsSubscribed] = React.useState(false);
  const [isAlreadySubscribed, setIsAlreadySubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: newsletterSubscribe,
    onSuccess: () => {
      setIsSubscribed(true);
      trackFormSubmitSuccess({
        form_id: 'newsletter',
        form_name: 'newsletter_form',
        form_destination: typeof window !== 'undefined' ? window.location.href : undefined,
        form_length: 2,
        send_to: generalLayout.marketing?.gtm_id,
      });
    },
    onError: (captchaRequestError) => { if (isCaptchaValidationError(captchaRequestError)) { setIsAlreadySubscribed(false); return; } setIsAlreadySubscribed(true); },
  });

  const locale = useLocaleStore((s) => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  const GDPRhref = getGdprUrl();

  return (
    <form
      className={cn(
        'relative z-10 flex w-full flex-col items-start justify-start gap-4 md:max-w-[451px]',
        'ea-homepage-newsletter__form',
      )}
      onSubmit={handleSubmit}
    >
<CaptchaRequestMessage error={subscribeMutation.error} />
      <div className="flex w-full flex-col items-start justify-start gap-2">
        <Heading tag="h3" color="white" title="Newsletter" />
        <Paragraph className="w-full" size="base" color="white">
          {t('text3675')}
        </Paragraph>
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-2">
        <Input
          aria-label={systemLabels('email')}
          id="newsletter-email"
          type="email"
          name="email"
          placeholder={`${t('text3676')}`}
          onChange={(e) => setEmail(e.target.value)}
          className={{
            wrapper: cn(
              'h-14 overflow-hidden rounded-[4px] bg-white',
              'ea-homepage-newsletter__form-item-email-input',
            ),
            main: cn(
              'placholder:text-[#9E9E9E] h-14 px-4 text-[13px] text-black sm:px-5 sm:text-sm lg:px-6',
            ),
          }}
          after={
            <Button
              type="submit"
              size="none"
              variant={'none'}
              className={cn(
                'mr-1 h-10 bg-walnut px-2 text-white sm:px-3',
                'ea-homepage-newsletter__form-item-email-submit-button',
              )}
              disabled={email.length === 0 || !checked || isSubscribed}
              isLoading={subscribeMutation.isPending}
            >
              {t('text182')}
            </Button>
          }
        />
        {isSubscribed && !isAlreadySubscribed && (
          <span className={cn('font-semibold text-success')}>{t('text3139')}</span>
        )}
        {isAlreadySubscribed && (
          <span className={cn('text-error font-semibold')}>{t('text234')}</span>
        )}
      </div>

      <Paragraph className="w-full" color="white" size="xs">
        {t('text3753')}{' '}
        <Link
          prefetch={false}
          target="_blank"
          rel="noopener noreferrer"
          href={GDPRhref}
          className="underline"
        >
          {t('text614')}
        </Link>{' '}
        {t('text3389')}
      </Paragraph>
    </form>
  );
}
